/**
 * Invitation acceptance page.
 *
 * Allows users to view, accept, or decline organization invitations.
 * Protected by (app) route group - auto-redirects to login with returnTo.
 */

import { captureEvent } from "@/lib/analytics-loader";
import {
  invitationQueryOptions,
  organizationKeys,
  useRejectInvitation,
} from "@/lib/queries/organization";
import { getCachedSession, sessionQueryOptions } from "@/lib/queries/session";
import { trpcClient } from "@/lib/trpc";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { AlertCircle, Check, Loader2, UserStar, X } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

const searchSchema = z.object({
  id: z.string().min(1, "Invitation ID is required"),
});

export const Route = createFileRoute("/invitations/accept")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ invitationId: search.id }),
  beforeLoad: async ({ context, location }) => {
    let session = getCachedSession(context.queryClient);

    if (session === undefined) {
      session = await context.queryClient.fetchQuery(sessionQueryOptions());
    }

    // Both user and session must exist for valid auth state
    if (!session?.user || !session?.session) {
      throw redirect({
        to: "/login",
        search: { returnTo: location.href },
      });
    }

    return { user: session.user, session };
  },
  loader: async ({ context, deps }) => {
    // prefetchQuery never throws (errors are swallowed into the query cache),
    // so awaiting won't block on failure — but it prevents an unawaited Promise
    // from surfacing as an unhandled rejection in Datadog. (EPD-6286)
    await context.queryClient.prefetchQuery(
      invitationQueryOptions(deps.invitationId),
    );
  },
  component: InvitationAcceptPage,
});

interface Invitation {
  id: string;
  email: string;
  role: "member" | "admin" | "owner";
  status: "pending" | "accepted" | "rejected" | "canceled";
  expiresAt: Date;
  organizationId: string;
  inviterId: string;
  // Flat fields from Better Auth API
  organizationName: string;
  organizationSlug: string;
  inviterEmail: string;
}

type AcceptState =
  | { status: "idle" }
  | { status: "accepting"; org: { name: string; slug: string } }
  | { status: "accepted"; org: { name: string; slug: string } }
  | { status: "error"; error: string };

export function InvitationAcceptPage() {
  const { id } = Route.useSearch();
  const { user } = Route.useRouteContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Track accept state independently from query/mutation states
  const [acceptState, setAcceptState] = useState<AcceptState>({
    status: "idle",
  });

  const {
    data: invitation,
    isLoading,
    error,
  } = useQuery({
    ...invitationQueryOptions(id),
    // Disable query once we start accepting
    enabled: acceptState.status === "idle",
    retry: false,
  });

  // Custom mutation that doesn't invalidate queries on success
  const acceptMutation = useMutation({
    mutationFn: (invitationId: string) =>
      trpcClient.organization.invitations.accept.mutate({ invitationId }),
  });

  const rejectMutation = useRejectInvitation();

  const inv = invitation as Invitation | undefined;

  // Check if invitation has expired
  const isExpired = inv?.expiresAt && new Date(inv.expiresAt) < new Date();

  async function handleAccept() {
    if (!inv) return;

    const org = { name: inv.organizationName, slug: inv.organizationSlug };

    // Set accepting state FIRST - this prevents error UI from showing
    setAcceptState({ status: "accepting", org });

    try {
      await acceptMutation.mutateAsync(id);

      // Invalidate organization queries so members/invitations lists refresh
      await queryClient.invalidateQueries({ queryKey: organizationKeys.all });

      // Show success state briefly before navigating
      setAcceptState({ status: "accepted", org });

      // Small delay so user sees the success message
      setTimeout(() => {
        navigate({ to: "/o/$orgSlug", params: { orgSlug: org.slug } });
      }, 1500);

      void captureEvent("invitation_accepted", {
        organization_name: org.name,
        organization_slug: org.slug,
        invitation_id: id,
      });
    } catch (err) {
      setAcceptState({
        status: "error",
        error: (err as Error)?.message ?? "Failed to accept invitation",
      });
    }
  }

  async function handleDecline() {
    try {
      await rejectMutation.mutateAsync(id);
      await queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      navigate({ to: "/" });
    } catch {
      // Error is displayed via rejectMutation.error in the UI
    }
  }

  // Accepted state - show success
  if (acceptState.status === "accepted") {
    const { org } = acceptState;
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <Check className="h-6 w-6 text-green-600" />
            </div>
            <CardTitle>Welcome to {org.name}!</CardTitle>
            <CardDescription>
              You've successfully joined the account.
            </CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Link to="/o/$orgSlug" params={{ orgSlug: org.slug }}>
              <Button>Go to {org.name}</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Accepting state - show loading
  if (acceptState.status === "accepting") {
    const { org } = acceptState;
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Loader2 className="h-6 w-6 text-primary animate-spin" />
            </div>
            <CardTitle>Joining {org.name}...</CardTitle>
            <CardDescription>
              Please wait while we add you to the account.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Accept error state
  if (acceptState.status === "error") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Failed to Join</CardTitle>
            <CardDescription>{acceptState.error}</CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Link to="/">
              <Button variant="outline">Go to Dashboard</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card className="w-full max-w-md animate-pulse">
          <CardHeader className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-muted" />
              <div className="space-y-2">
                <div className="h-5 w-32 bg-muted rounded" />
                <div className="h-4 w-24 bg-muted rounded" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="h-4 w-full bg-muted rounded" />
            <div className="h-4 w-3/4 bg-muted rounded" />
          </CardContent>
          <CardFooter className="gap-2">
            <div className="h-10 flex-1 bg-muted rounded" />
            <div className="h-10 flex-1 bg-muted rounded" />
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Error state - invitation not found
  if (error || !inv) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Invitation Not Found</CardTitle>
            <CardDescription>
              This invitation doesn't exist or you don't have permission to view
              it.
              {error && (
                <div className="mt-2 text-xs font-mono bg-destructive/10 p-2 rounded">
                  {(error as Error).message}
                </div>
              )}
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex-col gap-2">
            <Button asChild className="w-full">
              <a
                href={`/logout?returnTo=${encodeURIComponent(`/invitations/accept?id=${id}`)}`}
              >
                Sign in with different account
              </a>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to="/">Go to Dashboard</Link>
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Already accepted
  if (inv.status === "accepted") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <Check className="h-6 w-6 text-green-600" />
            </div>
            <CardTitle>Already Accepted</CardTitle>
            <CardDescription>
              You've already accepted this invitation to{" "}
              {inv.organizationName ?? "this account"}.
            </CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            {inv.organizationSlug ? (
              <Link to="/o/$orgSlug" params={{ orgSlug: inv.organizationSlug }}>
                <Button>Go to {inv.organizationName}</Button>
              </Link>
            ) : (
              <Link to="/">
                <Button variant="outline">Go to Dashboard</Button>
              </Link>
            )}
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Already rejected
  if (inv.status === "rejected") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <X className="h-6 w-6 text-muted-foreground" />
            </div>
            <CardTitle>Invitation Declined</CardTitle>
            <CardDescription>
              You've already declined this invitation.
            </CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Link to="/">
              <Button variant="outline">Go to Dashboard</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Canceled by organization
  if (inv.status === "canceled") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <AlertCircle className="h-6 w-6 text-muted-foreground" />
            </div>
            <CardTitle>Invitation Canceled</CardTitle>
            <CardDescription>
              This invitation has been canceled by the account.
            </CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Link to="/">
              <Button variant="outline">Go to Dashboard</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Expired
  if (isExpired) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
              <AlertCircle className="h-6 w-6 text-amber-600" />
            </div>
            <CardTitle>Invitation Expired</CardTitle>
            <CardDescription>
              This invitation has expired. Please contact the account admin for
              a new invitation.
            </CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Link to="/">
              <Button variant="outline">Go to Dashboard</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Wrong account - invitation was sent to a different email
  const isWrongEmail =
    inv?.email && inv.email.toLowerCase() !== user.email.toLowerCase();
  if (isWrongEmail) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
              <AlertCircle className="h-6 w-6 text-amber-600" />
            </div>
            <CardTitle>Wrong Account</CardTitle>
            <CardDescription>
              This invitation was sent to <strong>{inv.email}</strong>. You're
              signed in as <strong>{user.email}</strong>. Please sign in with
              the correct account to accept this invitation.
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex-col gap-2">
            <Button asChild className="w-full">
              <a
                href={`/logout?returnTo=${encodeURIComponent(`/invitations/accept?id=${id}`)}`}
              >
                Sign in with different account
              </a>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to="/">Go to Dashboard</Link>
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Pending invitation - show accept/decline UI
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
              <UserStar className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">
                {inv.organizationName ?? "Account"}
              </CardTitle>
              {inv.organizationSlug && (
                <CardDescription>/{inv.organizationSlug}</CardDescription>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="rounded-lg bg-muted/50 p-4 space-y-3">
            <p className="text-sm font-medium">
              You've been invited to join this account
            </p>

            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Invited by</span>
              <span className="font-medium">
                {inv.inviterEmail ?? "Account Admin"}
              </span>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Role</span>
              <Badge variant="secondary" className="capitalize">
                {inv.role}
              </Badge>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Expires</span>
              <span>
                {new Date(inv.expiresAt).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>

          {rejectMutation.error && (
            <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
              {(rejectMutation.error as Error)?.message ??
                "An error occurred. Please try again."}
            </div>
          )}
        </CardContent>

        <CardFooter className="gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleDecline}
            disabled={rejectMutation.isPending || acceptMutation.isPending}
          >
            {rejectMutation.isPending ? "Declining..." : "Decline"}
          </Button>
          <Button
            className="flex-1"
            onClick={handleAccept}
            disabled={acceptMutation.isPending || rejectMutation.isPending}
          >
            {acceptMutation.isPending ? "Accepting..." : "Accept Invitation"}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
